import {Button, Flex, notification, TableProps, Tooltip, Upload} from "antd";
import React, {useState} from "react";
import {BasicTable} from "../../Common/BasicComponents/BasicTable";
import {AdminHttpService} from "../../../services/Http/SearchTableService";
import {InsuranceProviderHttpService} from "../../../services/Http/InsuranceProviderHttpService";
import {FilterString} from "../../Common/BasicComponents/filters/FilterString";
import {FilterImageUpload} from "../../Common/BasicComponents/filters/FilterImageUpload";
import {InsuranceProviderEclaim} from "../../../models/InsuranceProvider";
import {PlusCircleOutlined, SafetyCertificateOutlined, UploadOutlined} from "@ant-design/icons";
import {AuthService} from "../../../services/AuthService";
import {useModal} from "../../../utils/modals";
import {BasicFormMethod, BasicFormModal} from "../../Common/BasicComponents/BasicFormModal";
import {PageTitle} from "../../Common/PageTitle/PageTitle";
import {useNavigate} from "react-router-dom";
import {Paths} from "../../../models/Paths";
import {useLogoBlob} from "../../Common/InfoLogo/useLogoBlob";
import {getLogoFromIntegrationCode} from "../../Common/logoUtils";

export interface InsuranceProviderManagementPageProps {
}

export type AnyObject = Record<string, any>;

function LogoCell({record, hasLogo}: { record: InsuranceProviderEclaim, hasLogo: boolean }) {
    const {blobUrl, failed} = useLogoBlob(record.id, hasLogo);

    if (blobUrl) {
        return <img src={blobUrl} height={32} style={{objectFit: 'contain', maxWidth: 80}} alt=""/>;
    }

    if (!failed) return null;

    const fallbackSrc = getLogoFromIntegrationCode(record.integrationCode);
    if (fallbackSrc) {
        return <img src={fallbackSrc} height={32} style={{objectFit: 'contain', maxWidth: 80}} alt=""/>;
    }

    return <span style={{color: '#aaa'}}>—</span>;
}

function AzioniCell({record, onLogoUpdated}: { record: InsuranceProviderEclaim, onLogoUpdated: (id: number) => void }) {
    const [uploading, setUploading] = useState(false);

    const handleUpload = async (file: File) => {
        setUploading(true);
        try {
            await InsuranceProviderHttpService.updateLogo(record.id, file);
            onLogoUpdated(record.id);
            notification.success({message: 'Logo aggiornato con successo'});
        } catch {
            notification.error({message: 'Errore durante l\'aggiornamento del logo'});
        } finally {
            setUploading(false);
        }
    };

    return (
        <Upload
            accept=".jpg,.jpeg,.png,.gif,.webp"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(file) => {
                handleUpload(file);
                return false;
            }}
        >
            <Tooltip title="Aggiorna logo">
                <Button icon={<UploadOutlined/>} size="small" loading={uploading}/>
            </Tooltip>
        </Upload>
    );
}

export function InsuranceProviderManagementPage<T extends AnyObject>(props:InsuranceProviderManagementPageProps) {
    const navigate = useNavigate();
    const {
        isOpen: isCreateFormModalOpen,
        open: openCreateFormModal,
        close: closeCreateFormModal
    } = useModal();

    const [tableVersion, setTableVersion] = useState(0);
    const [logoVersions, setLogoVersions] = useState<Record<number, number>>({});

    const handleLogoUpdated = (id: number) => {
        setLogoVersions(prev => ({...prev, [id]: (prev[id] ?? 0) + 1}));
    };

    const columns: TableProps<InsuranceProviderEclaim>['columns'] = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: 'Codice',
            dataIndex: 'code',
            key: 'code'
        },
        {
            title: 'Descrizione',
            dataIndex: 'description',
            key: 'description'
        },
        {
            title: 'Codice di Integrazione',
            dataIndex: 'integrationCode',
            key: 'integrationCode'
        },
        {
            title: 'Logo',
            key: 'logo',
            render: (_: any, record: InsuranceProviderEclaim) => {
                const effectiveHasLogo = record.hasLogo || (logoVersions[record.id] ?? 0) > 0;
                return (
                    <LogoCell
                        key={`logo-${record.id}-${logoVersions[record.id] ?? 0}`}
                        record={record}
                        hasLogo={effectiveHasLogo}
                    />
                );
            }
        },
        {
            title: 'Azioni',
            key: 'azioni',
            render: (_: any, record: InsuranceProviderEclaim) => (
                <AzioniCell record={record} onLogoUpdated={handleLogoUpdated}/>
            )
        }
    ]

    const searchFilters =[
        <FilterString dataKey={"code"} placeholder={"Codice"}></FilterString>,
        <FilterString dataKey={"description"} placeholder={"Descrizione"}></FilterString>,
        // <FilterString dataKey={"integrationCode"} placeholder={"Codice di Integrazione"}></FilterString>
    ]

    const formInputFields = [
        <FilterString dataKey={"code"} label={"Codice"} forceUpperCase={true} required={true}></FilterString>,
        <FilterString dataKey={"description"} label={"Descrizione"} forceUpperCase={true} required={true}></FilterString>,
        <FilterString dataKey={"integrationCode"} label={"Codice di Integrazione"} forceUpperCase={true} required={true}></FilterString>,
        <FilterImageUpload dataKey={"logo"} label={"Logo"} placeholder={"Seleziona logo (JPG, PNG, GIF, WEBP)"}></FilterImageUpload>
    ]

    return <>
        <Flex justify={"space-between"} align={"center"} style={{marginBottom: '8px'}}>
            <PageTitle title={"Gestione Assicurazioni"}></PageTitle>
            <Tooltip title={"Configura quali comportamenti speciali sono abilitati per ogni assicurazione"}>
                <Button
                    icon={<SafetyCertificateOutlined/>}
                    onClick={() => navigate(Paths.INSURANCE_PROVIDER_AUTHORIZATIONS)}
                >
                    Autorizzazioni Assicurazioni
                </Button>
            </Tooltip>
        </Flex>
        <BasicTable<InsuranceProviderEclaim>
            key={tableVersion}
            columns={columns}
            urlPath={AdminHttpService.URL_ADMIN_INSURANCE_PROVIDERS}
            filters={searchFilters}
        />

        <Button icon={<PlusCircleOutlined/>} type={"primary"} key="download"
                style={{float: 'right', marginTop: '10px', marginLeft: '10px'}}
                onClick={() => {
                    AuthService.isAdmin() && openCreateFormModal()
                }}>
            Aggiungi assicurazione
        </Button>

        {isCreateFormModalOpen &&
            <BasicFormModal<InsuranceProviderEclaim>
                urlPath={AdminHttpService.URL_ADMIN_INSURANCE_PROVIDERS}
                method={BasicFormMethod.CREATE}
                isOpen={isCreateFormModalOpen}
                onOk={closeCreateFormModal}
                onCancel={closeCreateFormModal}
                inputFields={formInputFields}
                title={"Aggiungi assicurazione"}
            />
        }
    </>

}